import { Modal } from "../../ui/Modal.tsx"
import { formatClock, formatMoney } from "../trading/format.ts"
import { ACCOUNT_CURRENCIES, withCurrency } from "../trading/newAccount.ts"
import { accountLabel } from "../strategies/attachRunner.ts"
import { replaySession } from "./replaySession.ts"
import type { PickedBar } from "./replaySession.ts"
import { useReplayStart } from "./useReplayStart.ts"

/**
 * "Replay from this bar" (docs/design/0007): which paper account replays it (a new
 * one starting at the bar, or an existing one moved forward to it), and whether
 * the chart's strategy paper trades it, so the backtest and paper trading run on
 * the same clock and can be compared trade by trade.
 */
export function ReplayStartDialog({ picked }: { readonly picked: PickedBar }) {
  const state = useReplayStart(picked)
  const { form } = state
  const close = () => replaySession.done()

  return (
    <Modal title={`Replay from ${formatClock(state.start)}`} onClose={close}>
      <form
        noValidate
        className="settings-form"
        aria-label="Start replay"
        onSubmit={(event) => {
          event.preventDefault()
          void state.submit().then((ok) => ok && close())
        }}
      >
        <p className="muted">
          {picked.instrument.id} · {picked.timeframe}: every open chart stops at this bar. Play / Step in the toolbar move
          them all together{state.step === "1m" ? ", one minute at a time, each showing its candle forming" : ""}; the paper
          account's orders, fills and strategy follow the same clock.
        </p>
        <fieldset disabled={state.created !== null}>
          <legend>Paper account</legend>
          <label className="style-check">
            <input type="radio" name="replay-account" checked={state.choice === "new"} onChange={() => state.setChoice("new")} />
            New account from this bar
          </label>
          <label className={state.reusable.length === 0 ? "style-check disabled" : "style-check"}>
            <input
              type="radio"
              name="replay-account"
              checked={state.choice === "existing"}
              disabled={state.reusable.length === 0}
              onChange={() => state.setChoice("existing")}
            />
            An existing account (moves forward to this bar)
          </label>
          {state.reusable.length === 0 && (
            <p className="muted">
              No replay account on {state.step} bars is at or before this bar (a replay only moves forward).
            </p>
          )}
        </fieldset>

        {state.choice === "new"
          ? (
            <>
              <label>
                Name
                <input
                  type="text"
                  value={form.name}
                  maxLength={64}
                  disabled={state.created !== null}
                  onChange={(event) => state.setForm({ ...form, name: event.target.value })}
                />
              </label>
              {state.errors.name && <p className="down">{state.errors.name}</p>}
              <label>
                Currency
                <select
                  value={form.currency}
                  disabled={state.created !== null}
                  onChange={(event) => state.setForm(withCurrency(form, event.target.value))}
                >
                  {ACCOUNT_CURRENCIES.map((currency) => (
                    <option key={currency} value={currency}>
                      {currency}
                      {currency === picked.instrument.currency ? " (symbol's)" : ""}
                    </option>
                  ))}
                </select>
              </label>
              {form.currency !== picked.instrument.currency && (
                <p className="muted">
                  Prices stay in {picked.instrument.currency}; profit and loss, charges and margin are converted to {form.currency} at the
                  previous day's ECB rate.
                </p>
              )}
              <label>
                Starting cash ({form.currency})
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.startingCash}
                  disabled={state.created !== null}
                  onChange={(event) => state.setForm({ ...form, startingCash: event.target.value })}
                />
              </label>
              {state.errors.startingCash && <p className="down">{state.errors.startingCash}</p>}
              <label>
                Charges
                <select
                  value={form.charges}
                  disabled={state.created !== null}
                  onChange={(event) => state.setForm({ ...form, charges: event.target.value === "Zero" ? "Zero" : "FyersNseEquity" })}
                >
                  {form.currency === "INR" && <option value="FyersNseEquity">Fyers NSE equity</option>}
                  <option value="Zero">None</option>
                </select>
              </label>
              {state.errors.charges && <p className="down">{state.errors.charges}</p>}
            </>
          )
          : (
            <label>
              Account
              <select value={state.existing?.id ?? ""} onChange={(event) => state.setExistingId(event.target.value)}>
                {state.reusable.map((account) => (
                  <option key={account.id} value={account.id}>
                    {accountLabel(account)} · at {account.clock._tag === "Replay" ? formatClock(account.clock.now) : ""} ·{" "}
                    {formatMoney(account.startingCash, account.currency)}
                  </option>
                ))}
              </select>
            </label>
          )}

        {state.strategy !== null && (
          <label className={state.canTrade ? "style-check" : "style-check disabled"}>
            <input type="checkbox" checked={state.trade} disabled={!state.canTrade} onChange={(event) => state.setTrade(event.target.checked)} />
            Paper trade the chart's strategy from this bar (long only, cash)
          </label>
        )}
        {state.tradeNote && <p className="muted">{state.tradeNote}</p>}
        {state.trade && (
          <p className="muted">
            From this bar the strategy trades with the account's starting cash as its capital (the bars before only warm
            up its indicators), in the paper account and in the Strategy Tester alike, so both start flat with the same money.
            {" "}With charges, the backtest pays them too (Fyers: 0.15% per fill, an estimate a little above the real
            ones){state.sizedDown ? ", and orders sized at 100% of equity are sized at 95% on both, so the paper account can pay the charges" : ""}.{" "}
            Its backtest trades and the paper fills are compared in the Strategy Tester's{" "}
            <strong>vs Paper</strong> tab.
          </p>
        )}
        {state.created !== null && (
          <p className="muted">{state.created.name} was created; Start tries the rest again on it.</p>
        )}
        {state.error && <p className="down" role="alert">{state.error}</p>}
        <footer className="settings-actions">
          <span className="toolbar-spacer" />
          <button type="button" onClick={close}>Cancel</button>
          <button type="submit" className="primary" disabled={state.busy || (state.choice === "existing" && state.existing === undefined)}>
            {state.busy ? "Starting…" : "Start replay"}
          </button>
        </footer>
      </form>
    </Modal>
  )
}
